import { Fragment, useContext, useEffect, useState } from "react";
import {
  Card,
  Loading,
  PaginationData,
  BaseTable,
  TableNumber,
  Button,
  PermissionLink,
  TableSorting,
  TableHeaderSort,
} from "../../../../components";
import { FilterSection } from "./components";
import { FilterType, TransferType } from "./types";
import { defaultFilter, useList } from "./utils";
import moment from "moment";
import { APPROVAL, convertNumber } from "utils";
import { IoSendSharp } from "react-icons/io5";
import { Delete } from "./components/Delete";
import { DataContext } from "../utils/contexts";

export function ItemsTransfer() {
  const [pagination, setPagination] = useState<FilterType>(defaultFilter);
  const { data, isLoading, refetch, } = useList(pagination);
   const { setFill, setActiveColumn } = useContext(DataContext);


  const header: TableSorting[] = [
    { title: "TRANSFER ID", value: "trx_code", isActive: true, colspan: 2 },
    { title: "DATE", value: "transaction_date", isActive: true },
    { title: "SOURCE LOCATION", value: "location_from", isActive: true },
    { title: "QTY TRANSFERED", value: "qty_transfer", isActive: true },
    { title: "DESTINATION LOCATION", value: "location_to", isActive: true },
    { title: "RECEIVED", value: "qty_received", isActive: true },
    { title: "REJECTED", value: "qty_rejected", isActive: true },
    { title: "TRADER", value: "employee_name", isActive: true },
    { title: "TRANSFER", value: "transfer_status", isActive: true },
    { title: "APPROVED", value: "approval_status", isActive: true },
    { title: "MEMO", value: "memo", isActive: true },
  ];


  const [tableActive, setTableActive] = useState<TableSorting[]>(header);

  useEffect(() => {
    setActiveColumn && setActiveColumn(tableActive);
    setFill({ 
        dateFrom: pagination.start_date, 
        dateTo: pagination.end_date,  
        approval_status: pagination.approval_status,
        transfer_status: pagination.transfer_status,
        from_location_id: pagination.from_location_id,
        to_location_id: pagination.to_location_id,
      });
  }, [
    setFill,
    pagination.start_date,
    pagination.end_date,
    pagination.approval_status,
    pagination.transfer_status,
    pagination.from_location_id,
    pagination.to_location_id,
    setActiveColumn,
    tableActive
  ]);

  return (
    <Fragment>
      <section className="my-2">
        {isLoading ? (
          <Loading loading />
        ) : (
          <>
            <section className="mt-2">
              <Card>
                <Card.Body>
                  <FilterSection
                    selected={pagination}
                    setSelected={setPagination}
                    setTableActive={setTableActive}
                    tableActive={tableActive}
                  />
                </Card.Body>
              </Card>
            </section>
            <section className="mt-2">
              <Card>
                <Card.Body>
                  <div className="overflow-x-auto max-h-[calc(100vh-600px)]">
                    <BaseTable className="border-collapse">
                      <thead className="bg-white sticky top-0 z-[0] !bg-black !text-white">
                        <tr>
                          <th >No</th>

                          <TableHeaderSort
                            title={tableActive}
                          />

                          <th>ACTION</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.payload.length === 0 ? (
                          <tr>
                            <td colSpan={14} className="text-center">
                              <Loading errorText="No data available" />
                            </td>
                          </tr>
                        ) : (
                          <Fragment>
                            {data?.payload?.map((item, key) => (
                              <tr key={item.transfer_id}>
                                <td>
                                  {TableNumber({
                                    pageCurrent: pagination?.page,
                                    dataLength: pagination?.limit!,
                                    index: key,
                                    limit: pagination?.limit,
                                    totalData: pagination?.total,
                                  })}
                                </td>

                                {/* Kolom filter */}
                                {tableActive
                                  .filter((col) => col.isActive !== false)
                                  .map((col, idx) => (
                                    <Fragment key={idx}>
                                      {renderCell(col.value!, item)}
                                    </Fragment>
                                  ))}

                                <td>
                                  <Delete id={item} refetch={refetch} />
                                </td>
                              </tr>
                            ))}
                          </Fragment>
                        )}
                      </tbody>
                    </BaseTable>
                    <div className="mt-5">
                      <PaginationData
                        data={data}
                        pagination={pagination}
                        setPagination={setPagination}
                      />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </section>
          </>
        )}
      </section>
    </Fragment>
  );
}


function renderCell(title: string, item: TransferType) {
  switch (title) {
    case "trx_code":
      return (
        <Fragment>
          <td className="text-center">
            {item.approval_status === APPROVAL.REJECTED ? (
              <Button type="button" color="ghost" disabled>
                <IoSendSharp />
              </Button>
            ) : (
              <PermissionLink
                permission="IT103"
                to={`/inventory/transaction/transfer-items/edit/${item.transfer_id}`}
              >
                <Button type="button" color="ghost">
                  <IoSendSharp />
                </Button>
              </PermissionLink>
            )}
          </td>
          <td>{item.trx_code}</td>
        </Fragment>
      );
    case "transaction_date":
      return <td>{moment(item.transaction_date).format("DD/MM/YYYY")}</td>;
    case "location_from":
      return <td>{item.location_from.location_name}</td>;
    case "qty_transfer":
      return <td className="text-center">{item.qty_transfer ? convertNumber(item.qty_transfer).intoNormalAmount : "0"}</td>;
    case "location_to":
      return <td>{item.location_to.location_name}</td>;
    case "qty_received":
      return <td className="text-center">{item.qty_received ? convertNumber(item.qty_received).intoNormalAmount : "0"}</td>;
    case "qty_rejected":
      return <td className="text-center">{item.qty_rejected ? convertNumber(item.qty_rejected).intoNormalAmount : "0"}</td>;
    case "employee_name":
      return <td>{item.employee.employee_name}</td>;
    case "transfer_status": {
      // BE inv_transfer: 1=Open, 2=Close (see transfer model comment)
      const ts = Number(item.transfer_status)
      const label = ts === 1 ? "Open" : ts === 2 ? "Closed" : ts === 3 ? "Completed" : "-"
      return <td>{label}</td>
    }
    case "approval_status":
      return <td>{APPROVAL[item.approval_status]}</td>;
    case "memo":
      return <td>{item.memo}</td>;
    default:
      return <></>;
  }
}
